import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createUISlice, UISlice } from '@/stores/new-checkout-state/ui-slice';
import { LineItemFulfillmentDetailsInput } from '@/gql';
import { CheckoutSteps } from '@/constants/checkout';
import { createCustomerSlice, CustomerSlice } from './customer-slice';
import { createShippingSlice, ShippingSlice } from './shipping-slice';
import { createPromoCodeSlice, PromoCodeSlice } from './promo-code-slice';
import { createCheckoutSlice, CheckoutSlice } from './checkout-slice';
import { createErrorSlice, ErrorSlice } from './error-slice';
import { createPaymentSlice, PaymentSlice } from './payment-slice';
import { createPricingSlice, PricingSlice } from './pricing-slice';

export interface NewCheckoutState
  extends UISlice,
    CustomerSlice,
    ShippingSlice,
    PromoCodeSlice,
    CheckoutSlice,
    ErrorSlice,
    PaymentSlice,
    PricingSlice {
  reset: () => void;
  resetCheckout: () => void;
  resetOnInit: () => void;

  storeSlug?: string;
  setStoreSlug: (slug: string) => void;

  storeName?: string;
  setStoreName: (name: string) => void;

  setInitCheckoutState: (payload: {
    storeSlug: string;
    storeName: string;
    lineItems: LineItemFulfillmentDetailsInput[];
  }) => void;
}

const useNewCheckoutState = create<NewCheckoutState>()(
  persist(
    (set, ...a) => ({
      ...createUISlice(set, ...a),
      ...createCustomerSlice(set, ...a),
      ...createShippingSlice(set, ...a),
      ...createPromoCodeSlice(set, ...a),
      ...createCheckoutSlice(set, ...a),
      ...createErrorSlice(set, ...a),
      ...createPaymentSlice(set, ...a),
      ...createPricingSlice(set, ...a),
      reset: () =>
        set({
          checkoutError: undefined,
          email: undefined,
          customerInfo: undefined,
          shippingRates: undefined,
          confirmationTokenId: undefined,
          shippingOption: 'fullStandardCost',
          checkoutId: undefined,
          checkoutRecord: undefined,
          checkoutValidated: false,
          promoCode: undefined,
          promoCodeError: undefined,
          shippingRatesValidated: false,
          customerInfoValidated: false,
          confirmationLookup: undefined,
          suggestedAddress: undefined,
          pricingInfo: undefined,
          userConfirmedAddress: false,
          currentCheckoutStep: 0,
          lineItems: undefined,
          paymentConfirmError: undefined,
          summaryLineItems: undefined,
        }),
      resetCheckout: () =>
        set({
          checkoutError: undefined,
          shippingRates: undefined,
          shippingOption: 'fullStandardCost',
          checkoutId: undefined,
          checkoutRecord: undefined,
          promoCode: undefined,
          promoCodeError: undefined,
          shippingRatesValidated: false,
          customerInfoValidated: false,
          confirmationLookup: undefined,
        }),
      resetOnInit: () =>
        set({
          shippingRatesValidated: false,
          currentCheckoutStep: CheckoutSteps.INIT,
          suggestedAddress: undefined,
          isVerifyingAddress: false,
          userConfirmedAddress: false,
        }),
      storeSlug: undefined,
      setStoreSlug: (slug: string) => set({ storeSlug: slug }),
      storeName: undefined,
      setStoreName: (name: string) => set({ storeName: name }),
      setInitCheckoutState: ({ storeSlug, storeName, lineItems }) =>
        set({
          storeSlug,
          storeName,
          lineItems,
        }),
    }),
    {
      name: 'new-checkout-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        email: state.email,
        customerInfo: state.customerInfo,
        shippingOption: state.shippingOption,
        checkoutRecord: state.checkoutRecord,
        promoCode: state.promoCode,
        checkoutId: state.checkoutId,
        storeSlug: state.storeSlug,
      }),
    }
  )
);

export default useNewCheckoutState;
